import { 
  Box, 
  Flex, 
  useDisclosure, 
  useColorModeValue, 
  useColorMode,
  IconButton,
  Container,
  Skeleton,
  Text,
  HStack,
  
} from '@chakra-ui/react';
import { ReactNode, useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminSidebar from '../components/admin/AdminSidebar';
import AdminHeader from '../components/admin/AdminHeader';
import { FiMenu } from 'react-icons/fi';
import { FaSearch } from 'react-icons/fa';
import AdminSearchModal from '../components/admin/AdminSearchModal';
import AdminSupportButton from '../components/admin/AdminSupportButton';
import AdminHealthIndicator from '../components/admin/AdminHealthIndicator';
import { logAction } from '../services/actionLog';
import '../styles/admin-scroll-fix.css';
import { useFastAdminScroll } from '../hooks/useFastAdminScroll';
import { usePublicSettings } from '../hooks/usePublicSettings';
import { assetUrl } from '../utils/url';
import { ThemeToggle } from '@/components/ui/theme-toggle';

interface AdminLayoutProps {
  children: ReactNode;
  requireAdmin?: boolean; // when false, allow any authenticated role
}

const AdminLayout = ({ children, requireAdmin = true }: AdminLayoutProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure({ defaultIsOpen: false });
  const { isOpen: isSearchOpen, onOpen: onSearchOpen, onClose: onSearchClose } = useDisclosure();
  
  // Track if sidebar was manually opened (to prevent auto-close)
  const manuallyOpenedRef = useRef(false);
  
  // Wrap onOpen and onClose with manual open tracking
  const handleOpen = () => {
    manuallyOpenedRef.current = true;
    onOpen();
  };
  
  const handleClose = () => {
    manuallyOpenedRef.current = false;
    onClose();
  };
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, user } = useAuth();
  const { colorMode } = useColorMode();
  const bg = useColorModeValue('gray.50', 'gray.900');
  const contentBg = useColorModeValue('white', '#1a1d29');
  const sidebarBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const { data: publicSettings } = usePublicSettings();
  
  // Track mobile state
  const [isMobile, setIsMobile] = useState(false);
  
  // Update mobile state on resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };
    checkMobile();
    const handleResize = () => checkMobile();
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile]);
  
  // Fast native-speed scroll retention (disabled on mobile)
  useFastAdminScroll(!isMobile);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login', { state: { from: location.pathname } });
    }
  }, [isAuthenticated, isLoading, navigate, location]);

  // Keyboard shortcut: Ctrl/Cmd+K opens admin search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const key = (e.key || '').toLowerCase();
      if ((e.ctrlKey || e.metaKey) && key === 'k') {
        e.preventDefault();
        onSearchOpen();
      }
      // Close sidebar on Escape key (mobile only)
      if (key === 'escape' && isOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onSearchOpen, isOpen, handleClose]);

  // Note: Removed auto-close on route change for mobile to prevent immediate closing after opening
  // Users can close the sidebar by clicking the overlay or pressing Escape

  // Redirect non-admin users only when page requires admin
  useEffect(() => {
    if (!isLoading && isAuthenticated && requireAdmin) {
      const role = (user as any)?.role;
      if (role && role !== 'admin') {
        navigate('/403', { replace: true, state: { from: location.pathname } });
      }
    }
  }, [isLoading, isAuthenticated, user, navigate, requireAdmin]);

  useEffect(() => {
    logAction({ type: 'nav', at: Date.now(), path: location.pathname + location.search });
  }, [location.pathname, location.search]);

  useEffect(() => {
    try {
      const raw = publicSettings?.club_logo_url || '/dist/img/logo-club-empty.svg';
      if (!raw) return;
      const url = assetUrl(raw) || raw;
      const setIcon = (rel: string) => {
        let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
        if (!link) {
          link = document.createElement('link');
          link.rel = rel as any;
          document.head.appendChild(link);
        }
        link.href = url;
        if (url.endsWith('.svg')) link.type = 'image/svg+xml';
      };
      setIcon('icon');
      setIcon('shortcut icon');
    } catch {}
  }, [publicSettings?.club_logo_url]);

  if (isLoading) {
    return (
      <Flex minH="100vh" bg={bg}>
        <Skeleton width="250px" height="100vh" position="fixed" />
        <Box ml={{ base: 0, md: '250px' }} flex={1} p={6}>
          <Skeleton height="60px" mb={6} />
          <Skeleton height="400px" />
        </Box>
      </Flex>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect in useEffect
  }

  return (
    <Box minH="100vh" bg={bg} className="admin-layout">
      {/* Mobile overlay */}
      <Box
        display={{ base: isOpen ? 'block' : 'none', md: 'none' }}
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="blackAlpha.600"
        zIndex={9}
        onClick={handleClose}
      />
        <AdminSidebar 
          isOpen={isOpen} 
          onClose={handleClose} 
          bg={sidebarBg}
          borderRight="1px"
          borderColor={borderColor}
        />
        <Box 
          ml={{ base: 0, md: '260px' }} 
          transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
          minH="100vh"
        >
          <AdminHeader 
            onMenuToggle={handleOpen} 
            rightContent={
              <HStack spacing={3}>
                <AdminHealthIndicator />
                <IconButton
                  aria-label="Vyhledávání v administraci"
                  icon={<FaSearch />}
                  onClick={onSearchOpen}
                  variant="ghost"
                  size="sm"
                  borderRadius="full"
                  _hover={{ bg: useColorModeValue('gray.100', 'gray.700'), transform: 'scale(1.05)' }}
                  transition="all 0.2s"
                />
                <AdminSupportButton />
                <ThemeToggle />
              </HStack>
            }
          />
          <Box as="main" className="admin-main" p={{ base: 4, md: 8 }} pb={{ base: 8, md: 12 }}>
            {children}
          </Box>
          {/* Admin Search Modal */}
          <AdminSearchModal
            isOpen={isSearchOpen}
            onClose={onSearchClose}
            onSelectPath={(path: string) => navigate(path)}
          />
        </Box>
    </Box>
  );
};

export default AdminLayout;
